import { type Component, createEffect, createMemo, createUniqueId, onCleanup, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import FieldMessage from '~/components/FieldMessage'
import { useFieldError } from '~/hooks/useFieldError'
import type { TextAreaPropsType } from '~/types/Textarea'
import { controlSize } from '~/utils/controlSize'

/** Tope a partir del cual el textarea deja de crecer y pasa a hacer scroll. */
const MAX_HEIGHT = 500

const Textarea: Component<TextAreaPropsType> = props => {
  const uid = createUniqueId()
  let textareaRef: HTMLTextAreaElement | undefined

  const [local, inputProps] = splitProps(props, ['label', 'size', 'class', 'placeholder', 'id', 'error', 'helperText'])

  const id = () => local.id ?? uid

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto.
  const resolvedError = useFieldError(
    () => (inputProps.name ? String(inputProps.name) : undefined),
    () => local.error
  )

  const sizeConfig = createMemo(() => controlSize(local.size))

  const textareaClasses = createMemo(() =>
    twMerge(
      'block rounded-lg w-full text-foreground bg-surface-muted border border-border-strong appearance-none focus:outline-none focus:ring-0 focus:border-primary-600 peer resize-none overflow-y-auto max-h-[500px]',
      resolvedError() ? 'border-danger focus:border-danger' : '',
      sizeConfig().padX,
      // Es multilínea: la altura la dan las filas y el contenido, así que la
      // escala sólo marca el arranque y el relleno.
      sizeConfig().minHeight,
      local.label ? sizeConfig().labelPad : sizeConfig().padY,
      local.label && local.placeholder ? 'placeholder:opacity-0 focus:placeholder:opacity-100 placeholder:transition-opacity' : '',
      sizeConfig().text,
      local.class
    )
  )

  /**
   * La etiqueta no se centra como en los campos de una línea: el textarea crece,
   * así que en reposo se queda a la altura de la primera línea y al escribir
   * sube al hueco de arriba.
   */
  const labelClasses = createMemo(() =>
    twMerge(
      'absolute top-1 translate-y-0 z-20 origin-left duration-100 transition-all pointer-events-none text-foreground',
      sizeConfig().padStart,
      sizeConfig().labelFloating,
      `${sizeConfig().labelRestingTop} peer-placeholder-shown:text-foreground`,
      sizeConfig().labelPeer,
      'peer-focus:top-1'
    )
  )

  /**
   * Ajusta el alto al contenido.
   *
   * Va en un efecto y no en un `addEventListener`: enganchado al evento sólo
   * reaccionaba a lo que se teclea, así que un valor puesto por programa —los
   * valores iniciales de un `<Form>`, un `reset()`— dejaba el campo en sus tres
   * filas con el texto escondido dentro.
   */
  const autoGrow = () => {
    if (!textareaRef) return
    textareaRef.style.height = 'auto'
    const scrollHeight = textareaRef.scrollHeight
    const capped = scrollHeight > MAX_HEIGHT
    textareaRef.style.height = capped ? `${MAX_HEIGHT}px` : `${scrollHeight}px`
    textareaRef.style.overflowY = capped ? 'auto' : 'hidden'
  }

  createEffect(() => {
    // Leer el valor deja el efecto suscrito a él, venga de donde venga.
    void inputProps.value
    autoGrow()
  })

  return (
    <div class="relative w-full">
      <textarea
        ref={el => {
          textareaRef = el
          // Listener propio en vez de un `onInput`: Solid une el del componente
          // con el que venga por props en el mismo manejador, y el de dentro
          // pisaría al de fuera sin decir nada.
          el.addEventListener('input', autoGrow)
          onCleanup(() => el.removeEventListener('input', autoGrow))
        }}
        {...inputProps}
        id={id()}
        rows="3"
        aria-invalid={resolvedError() ? 'true' : undefined}
        aria-describedby={`${id()}-message`}
        class={textareaClasses()}
        placeholder={local.placeholder || ' '}
      />
      <Show when={local.label}>
        {/* Bloque sólido que oculta el texto cuando hace scroll hacia arriba */}
        <div class="absolute top-px inset-x-px h-3.75 bg-surface-muted rounded-t-lg pointer-events-none z-10" />
        <label for={id()} class={labelClasses()}>
          {local.label}
        </label>
      </Show>
      <FieldMessage id={`${id()}-message`} size={local.size} error={resolvedError()} helperText={local.helperText} />
    </div>
  )
}

export default Textarea
